import React, { useState, useEffect, useMemo } from 'react';
import { 
  LayoutDashboard, Users, Package, FileText, Settings as SettingsIcon, Plus, Search, Trash2, 
  Save, X, Mail, Phone, DollarSign, ChevronLeft, Filter, Upload, Edit, Download, Database, 
  RefreshCw, Globe, User, CheckCircle, Send, ArrowLeft, ArrowRight, BarChart3, LogOut, Shield, 
  Cloud, FileSpreadsheet, Loader, Copy, Sun, Moon, MessageCircle, FileX, Bell, Clock, 
  AlertCircle, Check, MapPin, Hash, UserCheck, Smartphone, Tag
} from 'lucide-react';
import { THEMES, HOSTINGER_API_URL, CATEGORY_SORT_ORDER, DEFAULT_CATEGORIES, DEFAULT_TERMS, COMPANY_INFO, STATUS_OPTIONS } from '../config/constants';
import { apiCall } from '../services/api';
import BackgroundOrbs from '../components/BackgroundOrbs';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { generateCustomerKey, formatCurrency } from '../utils/helpers';

const DashboardView = ({ quotes, customers, setActiveTab, onQuoteClick, currentUser, onCreateNew, theme, users, onFollowUpClick }) => {
  const [dateRange, setDateRange] = useState('7days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  const uniqueStates = useMemo(() => [...new Set(customers.map(c => c.state).filter(Boolean))].sort(), [customers]);
  const sortedCustomers = useMemo(() => [...customers].sort((a,b) => (a.name || "").localeCompare(b.name || "")), [customers]);

  const filteredQuotes = useMemo(() => {
    let filtered = quotes;
    const now = new Date();

    if (selectedUser !== 'all') {
      filtered = filtered.filter(q => q.userId === selectedUser);
    } else if (currentUser.role !== 'admin') {
      filtered = filtered.filter(q => q.userId === currentUser.id);
    }

    if (selectedCustomer !== 'all') {
      filtered = filtered.filter(q => q.customerId === selectedCustomer);
    }

    if (selectedState !== 'all') {
      filtered = filtered.filter(q => {
        const customer = customers.find(c => c.id === q.customerId);
        return customer && customer.state === selectedState;
      });
    }

    if (searchQuery) {
      const qLower = searchQuery.toLowerCase();
      filtered = filtered.filter(q => (q.id || "").toLowerCase().includes(qLower));
    }

    filtered = filtered.filter(q => {
      if (!q.date) return false;
      const qDate = new Date(q.date);
      qDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      switch (dateRange) {
        case '7days': {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return qDate >= sevenDaysAgo && qDate <= today;
        }
        case '30days': {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return qDate >= thirtyDaysAgo && qDate <= today;
        }
        case 'thisMonth': 
          return qDate.getMonth() === today.getMonth() && qDate.getFullYear() === today.getFullYear();
        case 'lastMonth': {
             const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
             return qDate.getMonth() === lastMonth.getMonth() && qDate.getFullYear() === lastMonth.getFullYear();
        }
        case 'thisYear': 
          return qDate.getFullYear() === today.getFullYear();
        case 'custom': {
          if (!customStartDate || !customEndDate) return true;
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          return qDate >= start && qDate <= end;
        }
        default: return true;
      }
    });
    return filtered;
  }, [quotes, customers, dateRange, customStartDate, customEndDate, selectedUser, selectedCustomer, selectedState, searchQuery, currentUser]);

  const followUpData = useMemo(() => {
    const today = new Date();
    today.setHours(0,0,0,0);
    
    const pendingQuotes = (currentUser.role === 'admin' ? quotes : quotes.filter(q => q.userId === currentUser.id))
      .filter(q => q.status === 'Sent' || q.status === 'Follow Up');

    const result = { blue: [], orange: [], red: [], total: 0 };

    pendingQuotes.forEach(q => {
      const qDate = new Date(q.date);
      const diffTime = Math.abs(today - qDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= 7) result.red.push(q);
      else if (diffDays >= 5) result.orange.push(q);
      else if (diffDays >= 3) result.blue.push(q);
    });

    result.total = result.blue.length + result.orange.length + result.red.length;
    return result;
  }, [quotes, currentUser]);

  const chartData = useMemo(() => {
      const data = [0,0,0,0,0,0,0]; 
      const today = new Date();
      today.setHours(0,0,0,0);
      
      filteredQuotes.forEach(q => {
          const d = new Date(q.date);
          d.setHours(0,0,0,0);
          const diffTime = today - d;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays < 7 && diffDays >= 0) {
             data[6-diffDays] += (q.total || 0);
          }
      });
      return data;
  }, [filteredQuotes]);
  const maxVal = Math.max(...chartData) || 1;

  const totalCustomersCount = useMemo(() => {
    if (currentUser.role === 'admin') return customers.length;
    return customers.filter(c => c.userId === currentUser.id).length;
  }, [customers, currentUser]);

  return (
    <div className="p-8 space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6">
        <div>
          <p className={`text-lg ${THEMES[theme].textMuted} font-medium`}>Welcome back,</p>
          <h2 className={`text-5xl font-black ${THEMES[theme].heading} tracking-tighter mt-1`}>{currentUser?.name}</h2>
        </div>
        <Button variant="secondary" onClick={onCreateNew} theme={theme}><Plus size={20} /> New Quote</Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Card className={`lg:col-span-3 p-8 border-none ring-1 ring-slate-200/50 relative overflow-hidden`} theme={theme}>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
                  <div className="flex items-center gap-5">
                      <div className={`p-4 rounded-3xl bg-orange-100 text-orange-600`}>
                          <Clock size={32} />
                      </div>
                      <div>
                          <h3 className={`text-2xl font-black ${THEMES[theme].heading}`}>Follow-up Pipeline</h3>
                          <p className={THEMES[theme].textMuted}>Quotations requiring attention based on elapsed time.</p>
                      </div>
                  </div>

                  <div className="flex gap-4 w-full md:w-auto">
                      {[
                          { label: '3+ Days', count: followUpData.blue.length, countList: followUpData.blue, color: 'blue', blink: 'animate-blink-blue', icon: <Bell size={20} className="mb-2 text-blue-500" /> },
                          { label: '5+ Days', count: followUpData.orange.length, countList: followUpData.orange, color: 'orange', blink: 'animate-blink-orange', icon: <Clock size={20} className="mb-2 text-orange-500" /> },
                          { label: '7+ Days', count: followUpData.red.length, countList: followUpData.red, color: 'red', blink: 'animate-blink-red', icon: <AlertCircle size={20} className="mb-2 text-red-500" /> },
                      ].map((item, idx) => (
                          <button 
                              key={idx} 
                              onClick={() => item.count > 0 && onFollowUpClick(item.countList)}
                              className={`flex-1 md:w-32 p-4 rounded-2xl border bg-white/50 backdrop-blur-md flex flex-col items-center justify-center transition-all outline-none focus:ring-2 focus:ring-offset-2 ${item.count > 0 ? `${item.blink} border-${item.color}-200 cursor-pointer hover:scale-105 active:scale-95` : 'opacity-40 grayscale border-slate-100 cursor-default'}`}
                          >
                              {item.icon}
                              <span className={`text-2xl font-black text-${item.color}-600`}>{item.count}</span>
                              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{item.label}</span>
                          </button>
                      ))}
                  </div>
              </div>
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-orange-50/30 to-transparent pointer-events-none" />
          </Card>
      </div>

      <div className={`rounded-[2rem] border transition-all duration-300 bg-white/40 backdrop-blur-xl ${theme==='dark'?'border-slate-800':'border-white/50'} overflow-hidden shadow-sm`}>
        <div className={`p-6 flex justify-between items-center cursor-pointer ${showFilters ? 'border-b ' + (theme==='dark'?'border-slate-800':'border-white/30') : ''}`} onClick={() => setShowFilters(!showFilters)}>
            <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl text-blue-600"><Filter size={20}/></div>
                <div>
                    <span className={`font-bold ${THEMES[theme].text} block`}>Advanced Filters</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${theme==='dark'?'bg-slate-800 text-slate-400':'bg-white/50 text-slate-500 font-bold border border-white/40'}`}>{filteredQuotes.length} results found</span>
                </div>
            </div>
            <ChevronLeft className={`transition-transform duration-300 ${showFilters ? '-rotate-90' : ''} ${THEMES[theme].textMuted}`} size={20}/>
        </div>
        
        {showFilters && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
                <span className={`absolute left-3 top-3.5 text-slate-400`}><Search size={18}/></span>
                <input type="text" placeholder="Search Quote No..." className={`w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none ${THEMES[theme].input}`} value={searchQuery || ''} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <select value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)} className={`p-3 rounded-xl text-sm outline-none ${THEMES[theme].input}`}>
                <option value="all">All Customers</option>
                {sortedCustomers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} className={`p-3 rounded-xl text-sm outline-none ${THEMES[theme].input}`}>
                <option value="all">All States</option>
                {uniqueStates.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {currentUser.role === 'admin' ? (
                <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} className={`p-3 rounded-xl text-sm outline-none ${THEMES[theme].input}`}>
                    <option value="all">All Users</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
            ) : (
                <div className={`p-3 rounded-xl text-sm border border-dashed flex items-center text-slate-400 justify-center font-medium ${theme==='dark'?'border-slate-800':'border-white/30'}`}>
                    <User size={16} className="mr-2"/> My Quotes Only
                </div>
            )}
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className={`p-3 rounded-xl text-sm outline-none ${THEMES[theme].input}`}>
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="thisYear">This Year</option>
                <option value="custom">Custom Range</option>
                <option value="all">All Time</option>
            </select>
            {dateRange === 'custom' && (
                <>
                    <input type="date" className={`p-3 rounded-xl text-sm outline-none ${THEMES[theme].input}`} value={customStartDate || ''} onChange={e => setCustomStartDate(e.target.value)} placeholder="From" />
                    <input type="date" className={`p-3 rounded-xl text-sm outline-none ${THEMES[theme].input}`} value={customEndDate || ''} onChange={e => setCustomEndDate(e.target.value)} placeholder="To" />
                </>
            )}
            <button onClick={() => { setDateRange('7days'); setSelectedUser('all'); setSelectedCustomer('all'); setSelectedState('all'); setSearchQuery(''); setCustomStartDate(''); setCustomEndDate(''); }} className={`flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider hover:text-red-500 transition-colors ${THEMES[theme].textMuted}`}><RefreshCw size={14}/> Reset Filters</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[
          { label: 'Visible Quotes', val: filteredQuotes.length, icon: <FileText size={32}/>, color: 'text-blue-600', bg: 'bg-blue-100/60', action: () => setActiveTab('quotes') },
          { label: 'Total Customers', val: totalCustomersCount, icon: <Users size={32}/>, color: 'text-purple-600', bg: 'bg-purple-100/60', action: () => setActiveTab('customers') }
        ].map((stat, i) => (
            <div key={i} onClick={stat.action} className="cursor-pointer transition-transform hover:-translate-y-1">
                <Card className="p-8 flex items-center gap-6 h-full" theme={theme}>
                    <div className={`p-5 rounded-2xl ${stat.bg} ${stat.color} shadow-sm backdrop-blur-md`}>{stat.icon}</div>
                    <div>
                        <p className={`text-xs uppercase font-bold tracking-widest ${THEMES[theme].textMuted} mb-1`}>{stat.label}</p>
                        <p className={`text-4xl font-black ${THEMES[theme].heading}`}>{stat.val}</p>
                    </div>
                </Card>
            </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
         <Card className="lg:col-span-2 p-8" theme={theme}>
            <div className="flex items-center gap-4 mb-8"><div className="p-2 bg-blue-100/50 rounded-xl text-blue-600"><BarChart3 size={24}/></div><h3 className={`font-bold text-xl ${THEMES[theme].heading}`}>Trend (Filtered Data - Last 7 Days)</h3></div>
            <div className="h-64 flex items-end justify-between gap-4 px-2">
                {chartData.map((val, i) => (
                    <div key={i} className="flex-1 flex flex-col justify-end h-full group relative">
                        <div className="w-full bg-gradient-to-t from-blue-600 to-blue-300 rounded-t-2xl transition-all duration-700 ease-out opacity-80 group-hover:opacity-100 shadow-lg shadow-blue-500/20" style={{height: `${(val/maxVal)*100}%`, minHeight: '6px'}}></div>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity font-bold shadow-xl">{formatCurrency(val, 'INR')}</div>
                    </div>
                ))}
            </div>
         </Card>
         <Card className="p-8" theme={theme}>
            <div className="flex items-center gap-4 mb-8"><div className="p-2 bg-orange-100/50 rounded-xl text-orange-600"><FileText size={24}/></div><h3 className={`font-bold text-xl ${THEMES[theme].heading}`}>Filtered List</h3></div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
                {filteredQuotes.length > 0 ? filteredQuotes.slice(0, 10).map(q => (
                    <div key={q.id} onClick={() => onQuoteClick(q.id)} className={`p-4 rounded-2xl border ${theme === 'dark' ? 'border-slate-800 hover:bg-slate-800' : 'border-white/40 bg-white/30 hover:bg-white/50'} cursor-pointer transition-all group`}>
                        <div className="flex justify-between mb-1"><span className={`font-bold text-sm ${THEMES[theme].text} group-hover:text-blue-600 transition-colors`}>{q.id}</span><Badge status={q.status} theme={theme}/></div>
                        <div className={`text-xs ${THEMES[theme].textMuted} flex justify-between mt-2`}><span>{q.date}</span><span className="font-bold font-mono text-slate-600">{formatCurrency(q.total, q.currency)}</span></div>
                        <div className="text-[10px] text-slate-400 mt-1 truncate font-medium">{customers.find(c=>c.id===q.customerId)?.name}</div>
                    </div>
                )) : (
                    <div className={`text-center py-10 ${THEMES[theme].textMuted}`}>No quotes match filters.</div>
                )}
            </div>
         </Card>
      </div>
    </div>
  );
};

export default DashboardView;
